import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAbandoned } from './abandonedSlice';
import axios from '../../axiosConfig';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import Shell from '../dashboard/Shell';
import FeatureGate from '../../components/FeatureGate';
import { useToast } from '../../components/ui/Toast';
import './AbandonedCarts.css';

export default function AbandonedCarts() {
  const { isAuthenticated, loading } = useSecureAuth();
  const { items, status, error } = useSelector(s => s.abandoned);
  const dispatch = useDispatch();
  const { showToast } = useToast();

  const [activeTab, setActiveTab] = useState('carts');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedItems, setSelectedItems] = useState([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showNudgeModal, setShowNudgeModal] = useState(false);
  const [selectedCart, setSelectedCart] = useState(null);
  const [nudgeType, setNudgeType] = useState('email');
  const [nudgeMessage, setNudgeMessage] = useState('');

  useEffect(() => {
    if (isAuthenticated) dispatch(fetchAbandoned());
  }, [isAuthenticated, dispatch]);

  // Calculate stats
  const stats = useMemo(() => {
    const allCarts = items || [];
    const abandonedCarts = allCarts.filter(c => c.status === 'ABANDONED');
    const recoveredCarts = allCarts.filter(c => c.status === 'RECOVERED');
    const totalValue = abandonedCarts.reduce((sum, c) => sum + (parseFloat(c.subtotal) || 0), 0);

    return {
      total: allCarts.length,
      abandoned: abandonedCarts.length,
      recovered: recoveredCarts.length,
      recoveryRate: allCarts.length > 0 ? Math.round((recoveredCarts.length / allCarts.length) * 100) : 0,
      totalValue
    };
  }, [items]);

  // Filter items based on search, status, and tab (type)
  const filteredItems = useMemo(() => {
    let filtered = items || [];

    // Filter by tab type
    if (activeTab === 'checkout') {
      filtered = filtered.filter(item => item.type === 'CHECKOUT');
    } else {
      // "carts" tab shows CART type (or items without type for backwards compatibility)
      filtered = filtered.filter(item => !item.type || item.type === 'CART');
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.status === statusFilter.toUpperCase());
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        (item.customerEmail && item.customerEmail.toLowerCase().includes(query)) ||
        (item.customerPhone && item.customerPhone.includes(query)) ||
        (item.customerName && item.customerName.toLowerCase().includes(query)) ||
        (item.sessionId && item.sessionId.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [items, statusFilter, searchQuery, activeTab]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const past = new Date(date);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(filteredItems.map(item => item.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleViewDetails = (cart) => {
    setSelectedCart(cart);
    setShowDetailModal(true);
  };

  const handleNudge = (cart) => {
    setSelectedCart(cart);
    setNudgeType(cart.customerEmail ? 'email' : 'sms');
    setNudgeMessage(getDefaultNudgeMessage(cart));
    setShowNudgeModal(true);
  };

  const getDefaultNudgeMessage = (cart) => {
    const itemCount = cart.items?.length || 0;
    return `Hi${cart.customerName ? ` ${cart.customerName}` : ''}! You left ${itemCount} item${itemCount !== 1 ? 's' : ''} in your cart worth ${formatCurrency(cart.subtotal)}. Complete your purchase now and enjoy our latest offers!`;
  };

  const [sendingNudge, setSendingNudge] = useState(false);

  const handleSendNudge = async () => {
    if (!selectedCart) return;

    if (nudgeType === 'email' && !selectedCart.customerEmail) {
      showToast('No email address available for this customer', 'error');
      return;
    }
    if (nudgeType === 'sms' && !selectedCart.customerPhone) {
      showToast('No phone number available for this customer', 'error');
      return;
    }

    setSendingNudge(true);
    try {
      const res = await axios.post(`/abandoned-carts/${selectedCart.id}/send-recovery`, {
        channel: nudgeType,
        message: nudgeMessage
      });

      if (res.data.success) {
        showToast(`Recovery ${nudgeType === 'sms' ? 'SMS' : 'email'} sent to ${res.data.sentTo}`, 'success');
        // Update local state with recovery timestamp
        dispatch(fetchAbandoned());
      } else {
        showToast(res.data.error || 'Failed to send recovery message', 'error');
      }
    } catch (error) {
      const msg = error.response?.data?.error || 'Failed to send recovery message';
      showToast(msg, 'error');
    } finally {
      setSendingNudge(false);
      setShowNudgeModal(false);
      setSelectedCart(null);
    }
  };

  const handleBulkNudge = () => {
    showToast(`Sending recovery messages to ${selectedItems.length} customers...`, 'info');
    setSelectedItems([]);
  };

  // Loading state
  if (loading) {
    return (
      <Shell>
        <div className="ac-container">
          <div className="ac-loading">
            <div className="ac-spinner" />
            <p>Loading...</p>
          </div>
        </div>
      </Shell>
    );
  }

  // Auth check
  if (!isAuthenticated) {
    return (
      <Shell>
        <div className="ac-container">
          <div className="ac-loading">
            <p>Please log in to access abandoned carts.</p>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <FeatureGate featureId="marketing.abandoned_carts" upgradeMessage="Upgrade to access abandoned cart recovery and tracking">
        <div className="ac-container">
          {/* Header */}
          <div className="ac-header">
            <div className="ac-header-content">
              <h1>
                <div className="ac-header-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                Cart Recovery
              </h1>
              <p>Track abandoned carts and checkouts, send recovery messages to convert lost sales</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="ac-tabs">
            <button
              className={`ac-tab ${activeTab === 'carts' ? 'active' : ''}`}
              onClick={() => setActiveTab('carts')}
            >
              <svg className="ac-tab-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Abandoned Carts
              {stats.abandoned > 0 && <span className="ac-tab-badge">{stats.abandoned}</span>}
            </button>
            <button
              className={`ac-tab ${activeTab === 'checkout' ? 'active' : ''}`}
              onClick={() => setActiveTab('checkout')}
            >
              <svg className="ac-tab-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Abandoned Checkout
            </button>
          </div>

          {/* Stats Grid */}
          <div className="ac-stats-grid">
            <div className="ac-stat-card">
              <div className="ac-stat-icon total">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="ac-stat-info">
                <h3>Total Sessions</h3>
                <p className="ac-stat-value">{stats.total}</p>
              </div>
            </div>

            <div className="ac-stat-card">
              <div className="ac-stat-icon abandoned">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </div>
              <div className="ac-stat-info">
                <h3>Abandoned</h3>
                <p className="ac-stat-value">{stats.abandoned}</p>
              </div>
            </div>

            <div className="ac-stat-card">
              <div className="ac-stat-icon recovered">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ac-stat-info">
                <h3>Recovered</h3>
                <p className="ac-stat-value">{stats.recovered}</p>
                <span className={`ac-stat-change ${stats.recoveryRate >= 10 ? 'positive' : 'negative'}`}>
                  {stats.recoveryRate}% recovery rate
                </span>
              </div>
            </div>

            <div className="ac-stat-card">
              <div className="ac-stat-icon value">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ac-stat-info">
                <h3>Lost Revenue</h3>
                <p className="ac-stat-value">{formatCurrency(stats.totalValue)}</p>
              </div>
            </div>
          </div>

          {activeTab === 'carts' && (
            <>
              {/* Bulk Actions Bar */}
              {selectedItems.length > 0 && (
                <div className="ac-bulk-bar">
                  <div className="ac-bulk-info">
                    <span className="ac-bulk-count">{selectedItems.length} selected</span>
                  </div>
                  <div className="ac-bulk-actions">
                    <button className="ac-bulk-btn primary" onClick={handleBulkNudge}>
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Send Recovery
                    </button>
                    <button className="ac-bulk-btn secondary" onClick={() => setSelectedItems([])}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Toolbar */}
              <div className="ac-toolbar">
                <div className="ac-filters">
                  <div className="ac-search">
                    <svg className="ac-search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      className="ac-search-input"
                      placeholder="Search by email, phone, or name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <select
                    className="ac-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="abandoned">Abandoned</option>
                    <option value="recovered">Recovered</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
                <div className="ac-actions">
                  <button className="ac-btn ac-btn-secondary" onClick={() => dispatch(fetchAbandoned())}>
                    <svg className="ac-btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                  </button>
                </div>
              </div>

              {/* Loading State */}
              {status === 'loading' && (
                <div className="ac-table-container">
                  <div className="ac-loading">
                    <div className="ac-spinner" />
                    <p>Loading abandoned carts...</p>
                  </div>
                </div>
              )}

              {/* Error State */}
              {error && (
                <div className="ac-error">
                  <div className="ac-error-icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h3>Feature Being Migrated</h3>
                  <p>Abandoned cart tracking is being upgraded. Check back soon!</p>

                  <div className="ac-feature-info">
                    <h4>What Cart Recovery Will Do:</h4>
                    <ul className="ac-feature-list">
                      <li>
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Track customers who add items but don't checkout
                      </li>
                      <li>
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        View customer contact information when provided
                      </li>
                      <li>
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Send automated cart recovery emails and SMS
                      </li>
                      <li>
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Analyze abandonment patterns to optimize checkout
                      </li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Table */}
              {status === 'succeeded' && !error && (
                <div className="ac-table-container">
                  {filteredItems.length > 0 ? (
                    <table className="ac-table">
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}>
                            <input
                              type="checkbox"
                              className="ac-checkbox"
                              checked={selectedItems.length === filteredItems.length && filteredItems.length > 0}
                              onChange={handleSelectAll}
                            />
                          </th>
                          <th>Items</th>
                          <th>Customer</th>
                          <th>Value</th>
                          <th>Status</th>
                          <th>Last Active</th>
                          <th style={{ width: '100px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredItems.map((cart) => (
                          <tr key={cart.id}>
                            <td data-label="">
                              <input
                                type="checkbox"
                                className="ac-checkbox"
                                checked={selectedItems.includes(cart.id)}
                                onChange={() => handleSelectItem(cart.id)}
                              />
                            </td>
                            <td data-label="Items">
                              <div className="ac-cart-items">
                                <div className="ac-cart-images">
                                  {(cart.items || []).slice(0, 3).map((item, idx) => (
                                    item.image ? (
                                      <img
                                        key={idx}
                                        src={item.image}
                                        alt={item.name || 'Product'}
                                        className="ac-cart-img"
                                      />
                                    ) : (
                                      <div key={idx} className="ac-cart-img" />
                                    )
                                  ))}
                                  {(cart.items?.length || 0) > 3 && (
                                    <div className="ac-cart-more">+{cart.items.length - 3}</div>
                                  )}
                                </div>
                                <span className="ac-cart-count">{cart.items?.length || 0} items</span>
                              </div>
                            </td>
                            <td data-label="Customer">
                              <div className="ac-customer">
                                {cart.customerName && (
                                  <span className="ac-customer-name">{cart.customerName}</span>
                                )}
                                {(cart.customerEmail || cart.customerPhone) ? (
                                  <span className="ac-customer-contact">
                                    {cart.customerEmail ? (
                                      <>
                                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                        {cart.customerEmail}
                                      </>
                                    ) : (
                                      <>
                                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                        </svg>
                                        {cart.customerPhone}
                                      </>
                                    )}
                                  </span>
                                ) : (
                                  <span className="ac-customer-contact">
                                    Session: {cart.sessionId?.slice(0, 8)}...
                                  </span>
                                )}
                              </div>
                            </td>
                            <td data-label="Value">
                              <span className="ac-value">{formatCurrency(cart.subtotal)}</span>
                            </td>
                            <td data-label="Status">
                              <span className={`ac-status ${cart.status?.toLowerCase() || 'active'}`}>
                                <span className="ac-status-dot" />
                                {cart.status || 'ACTIVE'}
                              </span>
                            </td>
                            <td data-label="Last Active">
                              <div className="ac-time">
                                {new Date(cart.lastActionAt).toLocaleDateString()}
                                <span className="ac-time-ago">{formatTimeAgo(cart.lastActionAt)}</span>
                              </div>
                            </td>
                            <td data-label="Actions">
                              <div className="ac-row-actions">
                                <button
                                  className="ac-action-btn"
                                  title="View Details"
                                  onClick={() => handleViewDetails(cart)}
                                >
                                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                </button>
                                {(cart.customerEmail || cart.customerPhone) && cart.status !== 'RECOVERED' && (
                                  <button
                                    className="ac-action-btn"
                                    title="Send Recovery"
                                    onClick={() => handleNudge(cart)}
                                  >
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="ac-empty">
                      <div className="ac-empty-icon">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <h3>No abandoned carts yet</h3>
                      <p>When customers leave items in their cart without completing checkout, they'll appear here.</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {activeTab === 'checkout' && (
            <>
              {/* Toolbar */}
              <div className="ac-toolbar">
                <div className="ac-filters">
                  <div className="ac-search">
                    <svg className="ac-search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      className="ac-search-input"
                      placeholder="Search by email, phone, or name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <select
                    className="ac-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="abandoned">Abandoned</option>
                    <option value="recovered">Recovered</option>
                  </select>
                </div>
                <div className="ac-actions">
                  <button className="ac-btn ac-btn-secondary" onClick={() => dispatch(fetchAbandoned())}>
                    <svg className="ac-btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                  </button>
                </div>
              </div>

              {/* Table */}
              <div className="ac-table-container">
                {filteredItems.length > 0 ? (
                  <table className="ac-table">
                    <thead>
                      <tr>
                        <th>Items</th>
                        <th>Customer</th>
                        <th>Value</th>
                        <th>Status</th>
                        <th>Last Active</th>
                        <th style={{ width: '100px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredItems.map((cart) => (
                        <tr key={cart.id}>
                          <td data-label="Items">
                            <div className="ac-cart-items">
                              <div className="ac-cart-images">
                                {(cart.items || []).slice(0, 3).map((item, idx) => (
                                  item.image ? (
                                    <img key={idx} src={item.image} alt={item.name || 'Product'} className="ac-cart-img" />
                                  ) : (
                                    <div key={idx} className="ac-cart-img" />
                                  )
                                ))}
                                {(cart.items?.length || 0) > 3 && (
                                  <div className="ac-cart-more">+{cart.items.length - 3}</div>
                                )}
                              </div>
                              <span className="ac-cart-count">{cart.items?.length || 0} items</span>
                            </div>
                          </td>
                          <td data-label="Customer">
                            <div className="ac-customer">
                              {cart.customerName && <span className="ac-customer-name">{cart.customerName}</span>}
                              <span className="ac-customer-contact">
                                {cart.customerEmail && (
                                  <>
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    {cart.customerEmail}
                                  </>
                                )}
                              </span>
                            </div>
                          </td>
                          <td data-label="Value">
                            <span className="ac-value">{formatCurrency(cart.subtotal)}</span>
                          </td>
                          <td data-label="Status">
                            <span className={`ac-status ${cart.status?.toLowerCase() || 'active'}`}>
                              <span className="ac-status-dot" />
                              {cart.status || 'ACTIVE'}
                            </span>
                          </td>
                          <td data-label="Last Active">
                            <div className="ac-time">
                              {new Date(cart.lastActionAt).toLocaleDateString()}
                              <span className="ac-time-ago">{formatTimeAgo(cart.lastActionAt)}</span>
                            </div>
                          </td>
                          <td data-label="Actions">
                            <div className="ac-row-actions">
                              <button className="ac-action-btn" title="View Details" onClick={() => handleViewDetails(cart)}>
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </button>
                              {cart.customerEmail && cart.status !== 'RECOVERED' && (
                                <button className="ac-action-btn" title="Send Recovery" onClick={() => handleNudge(cart)}>
                                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="ac-empty">
                    <div className="ac-empty-icon">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <h3>No abandoned checkouts yet</h3>
                    <p>When customers reach the checkout page and provide their email but don't complete payment, they'll appear here.</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Detail Modal */}
        {showDetailModal && selectedCart && (
          <div className="ac-modal-overlay" onClick={() => setShowDetailModal(false)}>
            <div className="ac-modal" onClick={e => e.stopPropagation()}>
              <div className="ac-modal-header">
                <h2>Cart Details</h2>
                <button className="ac-modal-close" onClick={() => setShowDetailModal(false)}>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="ac-modal-body">
                <div className="ac-detail-section">
                  <div className="ac-detail-label">Customer</div>
                  <div className="ac-detail-value">
                    {selectedCart.customerName || 'Anonymous'}
                    {selectedCart.customerEmail && <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>{selectedCart.customerEmail}</div>}
                    {selectedCart.customerPhone && <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>{selectedCart.customerPhone}</div>}
                  </div>
                </div>

                <div className="ac-detail-section">
                  <div className="ac-detail-label">Status</div>
                  <span className={`ac-status ${selectedCart.status?.toLowerCase() || 'active'}`}>
                    <span className="ac-status-dot" />
                    {selectedCart.status || 'ACTIVE'}
                  </span>
                </div>

                <div className="ac-detail-section">
                  <div className="ac-detail-label">Cart Items ({selectedCart.items?.length || 0})</div>
                  <div className="ac-detail-items">
                    {(selectedCart.items || []).map((item, idx) => (
                      <div key={idx} className="ac-detail-item">
                        {item.image ? (
                          <img src={item.image} alt={item.name} className="ac-detail-item-img" />
                        ) : (
                          <div className="ac-detail-item-img" />
                        )}
                        <div className="ac-detail-item-info">
                          <div className="ac-detail-item-name">{item.name || 'Product'}</div>
                          <div className="ac-detail-item-meta">Qty: {item.quantity || 1}</div>
                        </div>
                        <div className="ac-detail-item-price">{formatCurrency(item.price)}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="ac-detail-section">
                  <div className="ac-detail-label">Total Value</div>
                  <div className="ac-detail-value" style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                    {formatCurrency(selectedCart.subtotal)}
                  </div>
                </div>
              </div>
              <div className="ac-modal-footer">
                <button className="ac-btn ac-btn-secondary" onClick={() => setShowDetailModal(false)}>
                  Close
                </button>
                {(selectedCart.customerEmail || selectedCart.customerPhone) && selectedCart.status !== 'RECOVERED' && (
                  <button
                    className="ac-btn ac-btn-primary"
                    onClick={() => {
                      setShowDetailModal(false);
                      handleNudge(selectedCart);
                    }}
                  >
                    <svg className="ac-btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    Send Recovery
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Nudge Modal */}
        {showNudgeModal && selectedCart && (
          <div className="ac-modal-overlay" onClick={() => setShowNudgeModal(false)}>
            <div className="ac-modal" onClick={e => e.stopPropagation()}>
              <div className="ac-modal-header">
                <h2>Send Recovery Message</h2>
                <button className="ac-modal-close" onClick={() => setShowNudgeModal(false)}>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="ac-modal-body">
                <div className="ac-nudge-form">
                  <div className="ac-form-group">
                    <label className="ac-form-label">Send via</label>
                    <div className="ac-nudge-options">
                      {selectedCart.customerEmail && (
                        <button
                          className={`ac-nudge-option ${nudgeType === 'email' ? 'selected' : ''}`}
                          onClick={() => setNudgeType('email')}
                        >
                          <div className="ac-nudge-option-icon">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <span className="ac-nudge-option-label">Email</span>
                        </button>
                      )}
                      {selectedCart.customerPhone && (
                        <button
                          className={`ac-nudge-option ${nudgeType === 'sms' ? 'selected' : ''}`}
                          onClick={() => setNudgeType('sms')}
                        >
                          <div className="ac-nudge-option-icon">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                          </div>
                          <span className="ac-nudge-option-label">SMS</span>
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="ac-form-group">
                    <label className="ac-form-label">
                      {nudgeType === 'email' ? 'Recipient Email' : 'Recipient Phone'}
                    </label>
                    <input
                      type="text"
                      className="ac-form-input"
                      value={nudgeType === 'email' ? selectedCart.customerEmail : selectedCart.customerPhone}
                      disabled
                    />
                  </div>

                  <div className="ac-form-group">
                    <label className="ac-form-label">Message</label>
                    <textarea
                      className="ac-form-textarea"
                      value={nudgeMessage}
                      onChange={(e) => setNudgeMessage(e.target.value)}
                      placeholder="Enter your recovery message..."
                    />
                  </div>
                </div>
              </div>
              <div className="ac-modal-footer">
                <button className="ac-btn ac-btn-secondary" onClick={() => setShowNudgeModal(false)}>
                  Cancel
                </button>
                <button className="ac-btn ac-btn-primary" onClick={handleSendNudge} disabled={sendingNudge}>
                  {sendingNudge ? (
                    <>
                      <div className="ac-spinner" style={{ width: 16, height: 16 }} />
                      Sending...
                    </>
                  ) : (
                    <>
                      <svg className="ac-btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Send Message
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </FeatureGate>
    </Shell>
  );
}
